import { Fragment, FunctionComponent, ReactNode, useCallback, useMemo, useState } from "react";
import { Tab } from "@headlessui/react";
import clsx from "clsx";

interface TabsProps {
  className?: string;
  tabs: string[] | { label: string; disabled?: boolean; hidden?: boolean }[];
  selectedIndex?: number;
  onChange?: (index: number) => void;
  children?: ReactNode | ((selectedIndex: number) => ReactNode);
  tabsClassName?: string;
}
const Tabs: FunctionComponent<TabsProps> = ({
  className,
  tabs,
  selectedIndex: controlledSelectedIndex,
  onChange,
  children,
  tabsClassName,
}) => {
  const formattedTabs = useMemo(
    () =>
      tabs.map((tab) =>
        typeof tab === "string"
          ? { label: tab, disabled: false, hidden: false }
          : { label: tab.label, disabled: !!tab.disabled, hidden: !!tab.hidden },
      ),
    [tabs],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const handleChange = useCallback(
    (index: number) => {
      if (controlledSelectedIndex === undefined) setSelectedIndex(index);
      if (onChange) onChange(index);
    },
    [controlledSelectedIndex, onChange],
  );

  const tabStyles = [
    "tw-border-solid",
    "tw-border-transparent",
    "tw-whitespace-nowrap",
    "tw-py-4",
    "tw-px-1",
    "tw-border-b-2",
    "tw-font-medium",
    "tw-text-sm",
    "focus:tw-outline-none",
  ];
  const tabStylesSelected = ["tw-text-indigo-800", "tw-border-b-indigo-800"];
  const tabStylesEnabled = [
    "tw-cursor-pointer",
    "tw-text-gray-500",
    "hover:tw-text-gray-700",
    "hover:tw-border-b-gray-300",
  ];
  const tabStylesDisabled = [
    "tw-cursor-not-allowed",
    "tw-text-gray-400",
    "tw-border-b-transparent",
  ];

  return (
    <Tab.Group
      as="div"
      className={className}
      selectedIndex={
        controlledSelectedIndex !== undefined ? controlledSelectedIndex : selectedIndex
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange={handleChange}
    >
      <Tab.List
        as="nav"
        className={clsx(
          "tw-shrink-0 tw-px-4 tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b tw-border-gray-200",
          tabsClassName,
        )}
      >
        <ul className="-tw-mb-px tw-flex tw-space-x-8 tw-list-none tw-p-0">
          {formattedTabs.map(({ label, disabled }, index) => (
            <Tab
              key={index}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              disabled={disabled}
              as="li"
              className={({ selected }) =>
                clsx(
                  tabStyles,
                  selected ? tabStylesSelected : disabled ? tabStylesDisabled : tabStylesEnabled,
                )
              }
            >
              {label}
            </Tab>
          ))}
        </ul>
      </Tab.List>
      <Tab.Panels as={Fragment}>
        {typeof children === "function"
          ? children(
              controlledSelectedIndex !== undefined ? controlledSelectedIndex : selectedIndex,
            )
          : children}
      </Tab.Panels>
    </Tab.Group>
  );
};

export default Tabs;
